import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  LTCPreferencesData,
  ltcPreferencesService,
} from '../../../../services/ltcPreferencesInfo';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('LTCPreferencesData');

type LTCPreferencesDataScreenRouteProp = RouteProp<RootStackParamList, 'LTCPreferencesData'>;

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
  ltcPreferencesId?: number;
};

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

type ListInfoItemProps = {
  label: string;
  items: string[];
};

const ListInfoItem: React.FC<ListInfoItemProps> = ({ label, items }) => {
  if (!items || items.length === 0) {
    return (
      <View style={styles.infoItem}>
        <Text style={styles.label}>{label}</Text>
        <Text style={styles.value}>Not specified</Text>
        <View style={styles.separator} />
      </View>
    );
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <View style={styles.listContainer}>
        {items.map((item, index) => (
          <View key={index} style={styles.listItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.listItemText}>{item}</Text>
          </View>
        ))}
      </View>
      <View style={styles.separator} />
    </View>
  );
};

export const LTCPreferencesDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<LTCPreferencesDataScreenRouteProp>();
  const [ltcPreferencesInfo, setLTCPreferencesInfo] = useState<LTCPreferencesData | null>(null);
  const [loading, setLoading] = useState(true);

  const ltcPreferencesId = route.params?.ltcPreferencesId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  useEffect(() => {
    loadLTCPreferencesInfo();
  }, []);

  const loadLTCPreferencesInfo = async () => {
    try {
      if (ltcPreferencesId) {
        const info = await ltcPreferencesService.getLTCPreferencesById(ltcPreferencesId, targetUserId);
        setLTCPreferencesInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await ltcPreferencesService.getAllLTCPreferences(targetUserId);
        if (allInfo.length > 0) {
          setLTCPreferencesInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading LTC preferences info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (ltcPreferencesInfo) {
      // Map the data from ltcPreferencesInfo to the format expected by LTCPreferencesInputScreen
      const mappedData = {
        id: ltcPreferencesInfo.id,
        inHomeCareSelectedOptions: ltcPreferencesInfo.inHomeCareSelectedOptions,
        inHomeCareAdditionalPreferences: ltcPreferencesInfo.inHomeCareAdditionalPreferences,
        adultDaycareSelectedOptions: ltcPreferencesInfo.adultDaycareSelectedOptions,
        adultDaycareAdditionalPreferences: ltcPreferencesInfo.adultDaycareAdditionalPreferences,
        assistedLivingSelectedOptions: ltcPreferencesInfo.assistedLivingSelectedOptions,
        assistedLivingAdditionalPreferences: ltcPreferencesInfo.assistedLivingAdditionalPreferences,
        memoryCareSelectedOptions: ltcPreferencesInfo.memoryCareSelectedOptions,
        memoryCareAdditionalPreferences: ltcPreferencesInfo.memoryCareAdditionalPreferences,
        nursingHomeSelectedOptions: ltcPreferencesInfo.nursingHomeSelectedOptions,
        nursingHomeAdditionalPreferences: ltcPreferencesInfo.nursingHomeAdditionalPreferences,
        hospiceSelectedOptions: ltcPreferencesInfo.hospiceSelectedOptions,
        hospiceAdditionalPreferences: ltcPreferencesInfo.hospiceAdditionalPreferences,
        respiteCareSelectedOptions: ltcPreferencesInfo.respiteCareSelectedOptions,
        respiteCareAdditionalPreferences: ltcPreferencesInfo.respiteCareAdditionalPreferences,
        continuedCareSelectedOptions: ltcPreferencesInfo.continuedCareSelectedOptions,
        continuedCareAdditionalPreferences: ltcPreferencesInfo.continuedCareAdditionalPreferences,
        doesNotApply: ltcPreferencesInfo.doesNotApply,
      };

      navigation.navigate('LTCPreferencesInput', {
        ltcPreferencesData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('LTCPreferencesInput');
    }
  };

  const handleDelete = () => {
    if (ltcPreferencesInfo) {
      Alert.alert(
        'Delete Long Term Care Preferences',
        'Are you sure you want to delete these long term care preferences? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await ltcPreferencesService.deleteLTCPreferences(ltcPreferencesInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if so
                const remainingData = await ltcPreferencesService.getAllLTCPreferences();
                if (!remainingData || remainingData.length === 0) {
                  await tasksService.resetTask('ltc_preferences');
                }

                (navigation as any).navigate('CarePreferences', { screen: 'CarePreferencesChecklist', params: { refreshKey: Date.now() } });
              } catch (error) {
                logger.error('Error deleting LTC preferences info:', error);
                Alert.alert('Error', 'Failed to delete long term care preferences');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    (navigation as any).navigate('CarePreferences', { screen: 'CarePreferencesChecklist', params: { refreshKey: Date.now() } });
  };

  const formatSelectedOptionsAsArray = (options?: string[], sectionKey?: string): string[] => {
    if (!options || options.length === 0) {
      return [];
    }

    // Map option IDs to their human-readable labels
    const PREFERENCES_DATA: Record<string, { options: { id: string; label: string }[] }> = {
      inHomeCare: {
        options: [
          { id: '1', label: 'Support with daily activities' },
          { id: '2', label: 'No Bathing' },
          { id: '3', label: 'No Cleaning' },
          { id: '4', label: 'Medication management' },
          { id: '5', label: 'Help Cooking' },
          { id: '6', label: 'I do not want in-home care' },
        ],
      },
      adultDaycare: {
        options: [
          { id: '1', label: 'Social activities' },
          { id: '2', label: 'Only on as needed basis' },
          { id: '3', label: 'Only for driving to and from' },
          { id: '4', label: 'Only for therapeutic services' },
          { id: '5', label: 'Help Cooking' },
          { id: '6', label: 'I do not want adult day care' },
        ],
      },
      assistedLiving: {
        options: [
          { id: '1', label: 'Close to family members' },
          { id: '2', label: 'Private room' },
          { id: '3', label: 'Shared room' },
          { id: '4', label: 'Pet-friendly facility' },
          { id: '5', label: 'Religious/spiritual services' },
          { id: '6', label: 'I do not want assisted living' },
        ],
      },
      memoryCare: {
        options: [
          { id: '1', label: 'Specialized memory care unit' },
          { id: '2', label: 'Secure environment' },
          { id: '3', label: 'Structured activities' },
          { id: '4', label: 'Family involvement encouraged' },
          { id: '5', label: 'Medical staff on-site' },
          { id: '6', label: 'I do not want memory care' },
        ],
      },
      nursingHome: {
        options: [
          { id: '1', label: '24/7 medical care' },
          { id: '2', label: 'Rehabilitation services' },
          { id: '3', label: 'Private room' },
          { id: '4', label: 'Shared room' },
          { id: '5', label: 'Family visitation' },
          { id: '6', label: 'I do not want nursing home care' },
        ],
      },
      hospice: {
        options: [
          { id: '1', label: 'Hospice at home' },
          { id: '2', label: 'Hospice facility' },
          { id: '3', label: 'Pain management focus' },
          { id: '4', label: 'Family support services' },
          { id: '5', label: 'Spiritual care' },
          { id: '6', label: 'I do not want hospice care' },
        ],
      },
      respiteCare: {
        options: [
          { id: '1', label: 'Short-term facility stay' },
          { id: '2', label: 'In-home respite care' },
          { id: '3', label: 'Adult daycare respite' },
          { id: '4', label: 'Emergency respite' },
          { id: '5', label: 'Regular scheduled respite' },
          { id: '6', label: 'I do not want respite care' },
        ],
      },
      continuedCare: {
        options: [
          { id: '1', label: 'CCRC (Continuing Care Retirement Community)' },
          { id: '2', label: 'Independent living to nursing care' },
          { id: '3', label: 'Guaranteed lifetime care' },
          { id: '4', label: 'Financial security' },
          { id: '5', label: 'Community amenities' },
          { id: '6', label: 'I do not want continued care' },
        ],
      },
    };

    if (!sectionKey || !PREFERENCES_DATA[sectionKey]) {
      // Fallback: return the IDs if we can't map them
      return options;
    }

    const sectionOptions = PREFERENCES_DATA[sectionKey].options;
    const labels = options.map(optionId => {
      const option = sectionOptions.find(opt => opt.id === optionId);
      return option ? option.label : optionId; // Fallback to ID if label not found
    });

    return labels;
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Long Term Care Preferences Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Long Term Care Preferences Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {ltcPreferencesInfo ? (
            <>
              <InfoItem label="Does Not Apply" value={ltcPreferencesInfo.doesNotApply} />

              {ltcPreferencesInfo.inHomeCareSelectedOptions && ltcPreferencesInfo.inHomeCareSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>In-Home Care Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.inHomeCareSelectedOptions, 'inHomeCare')} />
                  {ltcPreferencesInfo.inHomeCareAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.inHomeCareAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.adultDaycareSelectedOptions && ltcPreferencesInfo.adultDaycareSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Adult Daycare Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.adultDaycareSelectedOptions, 'adultDaycare')} />
                  {ltcPreferencesInfo.adultDaycareAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.adultDaycareAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.assistedLivingSelectedOptions && ltcPreferencesInfo.assistedLivingSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Assisted Living Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.assistedLivingSelectedOptions, 'assistedLiving')} />
                  {ltcPreferencesInfo.assistedLivingAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.assistedLivingAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.memoryCareSelectedOptions && ltcPreferencesInfo.memoryCareSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Memory Care Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.memoryCareSelectedOptions, 'memoryCare')} />
                  {ltcPreferencesInfo.memoryCareAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.memoryCareAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.nursingHomeSelectedOptions && ltcPreferencesInfo.nursingHomeSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Nursing Home Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.nursingHomeSelectedOptions, 'nursingHome')} />
                  {ltcPreferencesInfo.nursingHomeAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.nursingHomeAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.hospiceSelectedOptions && ltcPreferencesInfo.hospiceSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Hospice Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.hospiceSelectedOptions, 'hospice')} />
                  {ltcPreferencesInfo.hospiceAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.hospiceAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.respiteCareSelectedOptions && ltcPreferencesInfo.respiteCareSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Respite Care Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.respiteCareSelectedOptions, 'respiteCare')} />
                  {ltcPreferencesInfo.respiteCareAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.respiteCareAdditionalPreferences} />
                  )}
                </>
              )}

              {ltcPreferencesInfo.continuedCareSelectedOptions && ltcPreferencesInfo.continuedCareSelectedOptions.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Continued Care Preferences</Text>
                  <ListInfoItem label="Selected Options" items={formatSelectedOptionsAsArray(ltcPreferencesInfo.continuedCareSelectedOptions, 'continuedCare')} />
                  {ltcPreferencesInfo.continuedCareAdditionalPreferences && (
                    <InfoItem label="Additional Preferences" value={ltcPreferencesInfo.continuedCareAdditionalPreferences} />
                  )}
                </>
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No long term care preferences found.</Text>
              <TouchableOpacity
                style={[styles.button, styles.editButton, { marginTop: 20 }]}
                onPress={handleEdit}
              >
                <Text style={styles.buttonText}>Add Long Term Care Preferences</Text>
              </TouchableOpacity>
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginTop: ms(20),
    marginBottom: ms(15),
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
  listContainer: {
    marginTop: ms(4),
  },
  listItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: ms(6),
  },
  bullet: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginRight: ms(8),
    lineHeight: ms(22, 0.3),
  },
  listItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    flex: 1,
    lineHeight: ms(22, 0.3),
  },
});
